import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { DataTableColumn, DataTableRow } from './types';
import { getAlign, getSize } from './utils';
import Icon from '../icon';

export type DraggableTableRowProps = {
  columns: DataTableColumn[];
  row: DataTableRow;
  index: number;
  stickyFirstColumn?: boolean;
  onRowClick?: (row: DataTableRow) => void;
  onChangeOrder: (sourceId: string, targetId: string) => void;
  onDrop: () => void;
  dragType: string;
  dragEnabled: boolean;
};

type DragItem = {
  id: string;
  index: number;
};

const DraggableTableRow = ({
  columns,
  row,
  index,
  stickyFirstColumn = false,
  onRowClick,
  onChangeOrder,
  onDrop,
  dragType,
  dragEnabled,
}: DraggableTableRowProps) => {
  const dragHandleRef = useRef<HTMLDivElement>(null);

  const [{ isDragging }, dragRef, previewRef] = useDrag({
    type: dragType,
    item: (): DragItem => ({ id: row.id, index }),
    isDragging: (monitor) => row.id === monitor.getItem().id,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, dropRef] = useDrop({
    accept: dragType,
    hover: (source: DragItem) => {
      if (source.id === row.id) return;
      onChangeOrder(source.id, row.id);
    },
    drop: () => {
      onDrop();
    },
  });

  // Combine refs for the row
  const setRowRef = (node: HTMLTableRowElement | null) => {
    previewRef(node);
    dropRef(node);
  };

  // Attach drag ref to handle
  dragRef(dragHandleRef);

  const renderCell = (column: DataTableColumn) => {
    if (column.options?.render) {
      return column.options.render(row);
    }
    if (column.options?.renderLite) {
      return column.options.renderLite(row[column.key]);
    }
    return row[column.key];
  };

  const tdClassName =
    'tw-py-2 tw-px-2 ' +
    'tw-border-b tw-border-x-0 tw-border-t-0 tw-border-gray-100 tw-border-solid ' +
    'first:tw-pl-4 last:tw-pr-4 first:tw-border-l last:tw-border-r ' +
    (stickyFirstColumn
      ? 'first:tw-sticky first:tw-left-0 first:tw-z-10 first:tw-bg-white'
      : '');

  const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    if (onRowClick) {
      e.stopPropagation();
      onRowClick(row);
    }
  };

  return (
    <tr
      ref={setRowRef}
      className={`tw-table-row ${
        onRowClick ? 'tw-cursor-pointer hover:tw-bg-gray-50' : ''
      }`}
      onClick={handleRowClick}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      {columns.map((column, colIndex) => {
        // Check if this is the drag handle column
        if (column.key === 'drag_handle' && dragEnabled) {
          return (
            <td
              id={`drag-handle-${index}`}
              className={`${tdClassName} ${column?.className || ''}`}
              style={{
                width: getSize(column?.options?.size, columns),
              }}
            >
              <div
                ref={dragHandleRef}
                className="drag-handle tw-cursor-grab tw-flex tw-items-center
                tw-justify-center tw-text-gray-400 hover:tw-text-gray-600"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Icon type="drag_handle" />
              </div>
            </td>
          );
        }

        return (
          <td
            className={`${tdClassName} ${column?.className || ''}`}
            // eslint-disable-next-line react/no-array-index-key
            key={`${column.key}-${colIndex}`}
            style={{
              width: getSize(column?.options?.size, columns),
            }}
          >
            <div
              className={`tw-flex tw-flex-row tw-items-center tw-gap-2 ${getAlign(
                column.options?.align
              )}`}
            >
              {renderCell(column)}
            </div>
          </td>
        );
      })}
    </tr>
  );
};

export default DraggableTableRow;
